// Ethereal mood ritual — Feel (slider + hero) → Name → Body/Energy → Note → Save.
// Heroes: Oreia mood widgets (inspo / Higgsfield). Scoped dark theme on this route only.

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/SessionProvider';
import {
  bandForValence,
  BODY_OPTIONS,
  ENERGY_OPTIONS,
  moodKeyFromValence,
  postMoodCheckIn,
  type BodyKey,
  type EnergyKey,
} from '../api/mood';
import { Notice, Spinner } from '../components/ui';
import { IconArrow, IconLock } from '../components/Icons';

type Step = 'feel' | 'name' | 'depth' | 'note' | 'saved';

export function MoodRitualScreen() {
  const navigate = useNavigate();
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [step, setStep] = useState<Step>('feel');
  const [valence, setValence] = useState(0);
  const [energy, setEnergy] = useState<EnergyKey | null>(null);
  const [body, setBody] = useState<BodyKey | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const band = useMemo(() => bandForValence(valence), [valence]);
  const moodKey = moodKeyFromValence(valence);
  const progress = ((valence + 1) / 2) * 100;

  async function save() {
    if (!dek) return;
    setSaving(true);
    setError(null);
    try {
      await postMoodCheckIn({
        valence,
        moodKey,
        energyKey: energy,
        bodyKey: body,
        note: note.trim() || null,
        dek,
      });
      setStep('saved');
    } catch {
      setError('Could not save that check-in. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="omood-ritual">
      <div className="omood-ritual-top">
        <button type="button" className="omood-ritual-back" onClick={() => navigate('/self')} aria-label="Back to Self">
          <IconArrow style={{ width: 18, height: 18, transform: 'rotate(180deg)' }} />
        </button>
        <div className="olock">
          <IconLock /> Private
        </div>
      </div>

      {step === 'feel' || step === 'name' ? (
        <>
          <p className="omood-ritual-q">How do you feel right now?</p>
          <div className="omood-ritual-hero" aria-hidden="true">
            <svg className="omood-ritual-contours" viewBox="0 0 240 240"
              style={{ color: `oklch(0.78 0.09 ${285 - valence * 55})` }}>
              {[0, 1, 2, 3].map((ring) => (
                <ellipse key={ring} cx="120" cy="120" rx={(240 * (76 - ring * 15)) / 168} ry={(240 * (70 - ring * 15)) / 168}
                  transform="rotate(-24 120 120)" fill="none"
                  stroke="currentColor" strokeWidth="1.2" opacity={0.3 + ring * 0.16} />
              ))}
              <circle cx="120" cy="120" r="20" fill="currentColor" />
            </svg>
          </div>

          {step === 'feel' ? (
            <>
              <div className="omood-ritual-slider-wrap">
                <input
                  className="omood-ritual-slider"
                  type="range"
                  min={-1}
                  max={1}
                  step={0.01}
                  value={valence}
                  onChange={(e) => setValence(Number(e.target.value))}
                  aria-valuemin={-1}
                  aria-valuemax={1}
                  aria-valuenow={valence}
                  aria-label="Mood valence"
                  style={{ ['--mood-progress' as string]: `${progress}%` }}
                />
                <div className="omood-ritual-slider-labels">
                  <span>Very unpleasant</span>
                  <span>Very pleasant</span>
                </div>
              </div>
              <div className="omood-ritual-live">
                <h2 className="omood-ritual-name">{band.name}</h2>
                <p className="omood-ritual-blurb">{band.blurb}</p>
              </div>
              <button type="button" className="octa omood-ritual-cta" onClick={() => setStep('name')}>
                Continue
              </button>
            </>
          ) : (
            <>
              <div className="omood-ritual-live">
                <p className="olabel">Naming this</p>
                <h2 className="omood-ritual-name">{band.name}</h2>
                <p className="omood-ritual-blurb">{band.blurb}</p>
                <p className="ometa" style={{ marginTop: 12 }}>
                  Saved as <span className="omono">{moodKey}</span> · valence {valence.toFixed(2)}
                </p>
              </div>
              <div className="omood-ritual-actions">
                <button type="button" className="ochip" onClick={() => setStep('feel')}>
                  Adjust
                </button>
                <button type="button" className="octa" onClick={() => setStep('depth')}>
                  Looks right
                </button>
              </div>
            </>
          )}
        </>
      ) : null}

      {step === 'depth' ? (
        <div className="omood-ritual-panel">
          <h2 className="omood-ritual-name" style={{ fontSize: 28 }}>A little more depth</h2>
          <p className="omood-ritual-blurb">Optional — body and energy often say what the word does not.</p>

          <div className="olabel" style={{ marginTop: 22 }}>Energy</div>
          <div className="omood-strip" style={{ marginTop: 10 }}>
            {ENERGY_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`omood-chip${energy === o.key ? ' active' : ''}`}
                onClick={() => setEnergy((cur) => (cur === o.key ? null : o.key))}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="olabel" style={{ marginTop: 20 }}>Body</div>
          <div className="omood-strip" style={{ marginTop: 10 }}>
            {BODY_OPTIONS.map((o) => (
              <button
                key={o.key}
                type="button"
                className={`omood-chip${body === o.key ? ' active' : ''}`}
                onClick={() => setBody((cur) => (cur === o.key ? null : o.key))}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="omood-ritual-actions" style={{ marginTop: 28 }}>
            <button type="button" className="ochip" onClick={() => setStep('note')}>
              Skip
            </button>
            <button type="button" className="octa" onClick={() => setStep('note')}>
              Continue
            </button>
          </div>
        </div>
      ) : null}

      {step === 'note' ? (
        <div className="omood-ritual-panel">
          <h2 className="omood-ritual-name" style={{ fontSize: 28 }}>Anything to add?</h2>
          <p className="omood-ritual-blurb">
            A private note for today — sealed in your vault. Optional.
          </p>
          <textarea
            className="omood-ritual-note"
            rows={5}
            maxLength={2000}
            placeholder="What is this feeling connected to?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          {error ? (
            <div style={{ marginTop: 12 }}>
              <Notice tone="danger">{error}</Notice>
            </div>
          ) : null}
          <div className="omood-ritual-actions" style={{ marginTop: 22 }}>
            <button type="button" className="ochip" onClick={() => setStep('depth')}>
              Back
            </button>
            <button type="button" className="octa" disabled={saving} onClick={() => void save()}>
              {saving ? <Spinner label="Saving…" /> : 'Save check-in'}
            </button>
          </div>
        </div>
      ) : null}

      {step === 'saved' ? (
        <div className="omood-ritual-panel omood-ritual-saved">
          <p className="olabel">Logged</p>
          <h2 className="omood-ritual-name">{band.name}</h2>
          <p className="omood-ritual-blurb">{band.blurb}</p>
          <p className="ometa" style={{ marginTop: 14 }}>
            {[moodKey, energy, body].filter(Boolean).join(' · ')}
          </p>
          <div className="omood-ritual-actions" style={{ marginTop: 28, flexDirection: 'column' }}>
            <Link className="octa" to="/self" style={{ textDecoration: 'none' }}>
              Back to Self
            </Link>
            <Link className="ochip" to="/write" style={{ textDecoration: 'none' }}>
              Write about this
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
